import { DIFFICULTIES } from "./config";
import { noteQuarterDuration } from "../book-practice-score";
import * as automataTypes from "../../consts/raTypes";
import { parseYouTubeUrl } from "./youtube";
import { ROOT, MAX_BRANCHES, MAX_DEPTH, MAX_TREE_MEASURES } from "../rhythm-tree";
import { KEY_SPECS, PIANO_LOW, PIANO_HIGH, midi } from "../piano";
import { MAX_PARTS, INSTRUMENT_IDS, baseOf, isPartKey, voicesFor } from "../instruments";
import { DYNAMICS, HAIRPINS, measureMarkings } from "../score-structure";
import { RECORDING_SOUNDS, SOUND_INSTRUMENTS } from "../ideas/sounds";
import { MAX_TAKES, TAKE_RULES } from "../takes";
import { HOWS } from "../ideas/kinship";
import { isRef } from "../stats/refs";

export { RECORDING_SOUNDS, SOUND_INSTRUMENTS };

export class SocialError extends Error {
  constructor(status, message) { super(message); this.status = status; }
}
export function fail(status, message) { throw new SocialError(status, message); }
export function string(value, max, label, required = false) {
  if (value == null && !required) return "";
  if (typeof value !== "string" || value.trim().length > max || (required && !value.trim())) fail(400, `Enter a valid ${label} (up to ${max} characters).`);
  return value.trim();
}
export function enumValue(value, allowed, label) {
  if (!allowed.includes(value)) fail(400, `Choose a valid ${label}.`);
  return value;
}
export function normalizeProfile(body) {
  const handle = string(body.handle, 24, "handle", true).toLowerCase();
  if (!/^[a-z0-9][a-z0-9_]{2,23}$/.test(handle) || ["admin", "truechops", "support", "account", "api"].includes(handle)) fail(400, "Use 3-24 letters, numbers or underscores for your handle. This handle may be reserved.");
  if (!Array.isArray(body.instruments) || body.instruments.length > 5) fail(400, "Choose your instruments.");
  return { handle, displayName: string(body.displayName, 60, "display name", true), bio: string(body.bio, 360, "bio"),
    instruments: [...new Set(body.instruments.map((value) => enumValue(value, INSTRUMENT_IDS, "instrument")))] };
}
// The tools someone pins to the shelf in Make (docs/refactor-plan.md, Phase 2): up to 12 tool ids,
// or public modules (module:<id>).
export function normalizeToolPins(body) {
  if (!Array.isArray(body?.pins) || body.pins.length > 12) fail(400, "Pin up to 12 tools.");
  for (const pin of body.pins) if (typeof pin !== "string" || !/^([a-z][a-z0-9-]{0,39}|module:[a-f0-9]{24})$/.test(pin)) fail(400, "Choose a tool to pin.");
  return [...new Set(body.pins)];
}
export function normalizeMetadata(body) {
  const rawTags = typeof body.tags === "string" ? body.tags.split(",").filter((tag) => tag.trim()) : body.tags || [];
  if (!Array.isArray(rawTags) || rawTags.length > 8) fail(400, "Use up to eight tags.");
  const tags = [...new Set(rawTags.map((tag) => string(tag, 24, "tag", true).toLowerCase()))];
  const tempo = body.tempo ?? 90;
  if (!Number.isFinite(tempo) || tempo < 20 || tempo > 300) fail(400, "Tempo must be between 20 and 300 BPM.");
  const youtubeInput = string(body.youtubeUrl, 2048, "YouTube link");
  const youtube = parseYouTubeUrl(youtubeInput);
  if (youtubeInput && !youtube) fail(400, "Enter a valid YouTube video link (watch, share, Shorts or live).");
  return { name: string(body.name, 100, "rhythm title", true), description: string(body.description, 1000, "description"), tags,
    youtubeUrl: youtube?.url || "",
    tempo, difficulty: enumValue(body.difficulty || "beginner", DIFFICULTIES, "difficulty"),
    visibility: enumValue(body.visibility || "private", ["private", "public"], "visibility") };
}
export function validateScore(score) {
  if (!score || !Array.isArray(score.measures) || !score.measures.length || score.measures.length > 64) fail(400, "A rhythm needs 1-64 measures.");
  const instruments = new Set();
  const measures = score.measures.map((measure) => {
    const takes = measureTakes(measure, score.parts);
    const num = measure?.timeSig?.num, type = measure?.timeSig?.type;
    if (!Number.isInteger(num) || num < 1 || num > 32 || ![1,2,4,8,16,32].includes(type)) fail(400, "Invalid time signature.");
    if (!Array.isArray(measure.parts) || !measure.parts.length || measure.parts.length > MAX_PARTS) fail(400, "Invalid score parts.");
    const seen = new Set();
    const parts = measure.parts.map((part) => {
      const instrument = isPartKey(part.instrument) ? part.instrument : fail(400, "Choose a valid instrument.");
      if (seen.has(instrument)) fail(400, "A measure cannot repeat an instrument part.");
      seen.add(instrument); instruments.add(instrument);
      if (!Array.isArray(part.voices) || !part.voices.length || part.voices.length > 4) fail(400, "Invalid voices.");
      // A grand staff has a voice for each staff: a piano's right hand (treble), then its left (bass).
      const piano = baseOf(instrument) === "piano";
      if (voicesFor(instrument) === 2 && part.voices.length !== 2) fail(400, piano ? "A piano part needs a right hand and a left hand." : "This part needs both its staffs.");
      return { instrument, voices: part.voices.map((voice) => {
        if (!Array.isArray(voice.notes) || !voice.notes.length || voice.notes.length > 256) fail(400, "Invalid notes.");
        const notes = voice.notes.map((note) => {
          if (!Array.isArray(note.notes) || note.notes.length > 8 || note.notes.some((pitch) => typeof pitch !== "string" || !/^[A-G][#b]?-?\d$/.test(pitch)) ||
            ![1,2,4,8,16,32,64,128].includes(note.duration) || ![0,1,2,3].includes(note.dots || 0) ||
            (note.velocity != null && (!Number.isFinite(note.velocity) || note.velocity < 0 || note.velocity > 1))) fail(400, "Invalid note values.");
          if (piano && note.notes.some((pitch) => !(midi(pitch) >= PIANO_LOW && midi(pitch) <= PIANO_HIGH))) fail(400, "Piano notes must be on the keyboard (A0 to C8).");
          const ornaments = string(note.ornaments, 16, "ornaments");
          if (!/^[afdcblrstmunvop]*$/.test(ornaments)) fail(400, "Invalid ornaments.");
          const head = note.head || "";
          if (head && !["d1","d3","t1","t3","x1","x2","x3","n"].includes(head)) fail(400, "Invalid note head.");
          const dynamic = note.dynamic == null || note.dynamic === "" ? null : enumValue(note.dynamic, DYNAMICS, "dynamic");
          if (note.slur != null && !["start", "end"].includes(note.slur)) fail(400, "Invalid slur.");
          const text = string(note.text, 32, "note text"), lyric = string(note.lyric, 24, "lyric");
          return { notes: [...note.notes], duration: note.duration, dots: note.dots || 0, ornaments, velocity: note.velocity ?? 0.5, ...(head ? { head } : {}), ...(dynamic ? { dynamic } : {}),
            ...(note.tie ? { tie: true } : {}), ...(note.slur ? { slur: note.slur } : {}), ...(text ? { text } : {}), ...(lyric ? { lyric } : {}) };
        });
        if (voice.tuplets != null && (!Array.isArray(voice.tuplets) || voice.tuplets.length > 64)) fail(400, "Invalid tuplets.");
        const tuplets = (voice.tuplets || []).map(({ actual, normal, start, end }) => {
          if (![actual,normal,start,end].every(Number.isInteger) || actual < 2 || actual > 32 || normal < 1 || normal > 32 || start < 0 || end <= start || end > notes.length) fail(400, "Invalid tuplet values.");
          return { actual, normal, start, end };
        });
        const duration = notes.reduce((sum,n,i) => sum + noteQuarterDuration(n,i,tuplets),0);
        if (Math.abs(duration - num*4/type) > 0.00001) fail(400, "Every voice must fill its measure before saving.");
        // Crescendos and diminuendos, over a range of the voice's notes.
        if (voice.hairpins != null && (!Array.isArray(voice.hairpins) || voice.hairpins.length > 16)) fail(400, "Invalid hairpins.");
        // A hairpin left outside the notes (after the rhythm was varied) is dropped.
        const hairpins = (voice.hairpins || []).map(({ type: kind, start, end }) => {
          if (!HAIRPINS.includes(kind) || !Number.isInteger(start) || !Number.isInteger(end)) fail(400, "Invalid hairpins.");
          return { type: kind, start, end };
        }).filter(({ start, end }) => start >= 0 && end > start && end <= notes.length);
        return { notes, tuplets, ...(hairpins.length ? { hairpins } : {}) };
      }) };
    });
    return { timeSig: { num, type }, parts, ...measureMarkings(measure, (message) => fail(400, message)), ...takes };
  });
  const keySig = score.keySig == null || score.keySig === "" ? null : enumValue(score.keySig, KEY_SPECS, "key signature");
  const named = (name) => { const given = string(score.parts?.[name]?.name, 40, "part name"); return given ? { name: given } : {}; };
  return { score: { parts: Object.fromEntries([...instruments].map((name) => [name, { enabled: score.parts?.[name]?.enabled !== false, ...named(name) }])), measures, ...(keySig && keySig !== "C" ? { keySig } : {}), ...(score.concert ? { concert: true } : {}) },
    instruments: [...new Set([...instruments].map(baseOf))] };
}
// A bar's takes (src/lib/takes.js), checked as bars in its own time signature; with fewer than two
// it's a bar like any other. { takes, takeId, takeRule }, or nothing.
function measureTakes(measure, partConfig) {
  if (measure?.takes == null) return {};
  if (!Array.isArray(measure.takes) || measure.takes.length > MAX_TAKES) fail(400, `A bar can have up to ${MAX_TAKES} takes.`);
  if (measure.takes.length < 2) return {};
  const ids = new Set();
  const takes = measure.takes.map((take) => {
    const id = string(take?.id, 8, "take", true);
    if (!/^t\d{1,3}$/.test(id) || ids.has(id)) fail(400, "Invalid take.");
    ids.add(id);
    const bar = take.score?.measures?.[0];
    if (!Array.isArray(take.score?.measures) || take.score.measures.length !== 1 || bar?.timeSig?.num !== measure.timeSig?.num || bar?.timeSig?.type !== measure.timeSig?.type) fail(400, "A take must be one bar in its bar's time signature.");
    const { score } = validateScore({ parts: partConfig, measures: [{ timeSig: bar.timeSig, parts: bar.parts }] });
    const similarity = take.similarity == null ? null : Number(take.similarity);
    if (similarity != null && !(Number.isFinite(similarity) && similarity >= 0 && similarity <= 100)) fail(400, "Invalid take.");
    if (take.ref != null && !isRef(take.ref)) fail(400, "Invalid take.");
    return { id, score: { measures: [{ timeSig: score.measures[0].timeSig, parts: score.measures[0].parts }] }, ...(take.ref ? { ref: take.ref } : {}),
      ...(HOWS.includes(take.how) ? { how: take.how } : {}), ...(similarity != null ? { similarity: Math.round(similarity) } : {}) };
  });
  if (!ids.has(measure.takeId)) fail(400, "Invalid take.");
  const rule = measure.takeRule || {}, minSimilarity = rule.minSimilarity == null ? null : Number(rule.minSimilarity);
  if (minSimilarity != null && !(Number.isFinite(minSimilarity) && minSimilarity >= 0 && minSimilarity <= 100)) fail(400, "Invalid take rule.");
  return { takes, takeId: measure.takeId, takeRule: { play: TAKE_RULES.some(([play]) => play === rule.play) ? rule.play : "shuffle", minSimilarity: minSimilarity == null ? null : Math.round(minSimilarity),
    pinned: rule.pinned && ids.has(rule.pinned) ? rule.pinned : null } };
}
// A rhythm's variation tree: named branches, each a full score, listed parent-first.
export function normalizeTree(value) {
  if (value == null) return null;
  if (typeof value !== "object" || !Array.isArray(value.nodes)) fail(400, "Invalid rhythm tree.");
  if (value.nodes.length > MAX_BRANCHES) fail(400, `A rhythm tree can have up to ${MAX_BRANCHES} branches.`);
  const depth = new Map([[ROOT, 0]]);
  let measures = 0;
  const nodes = value.nodes.map((node) => {
    const id = string(node?.id, 40, "branch ID", true), parentId = string(node.parentId, 40, "branch parent", true);
    if (!/^[A-Za-z0-9_-]+$/.test(id) || depth.has(id)) fail(400, "Invalid rhythm tree.");
    if (!depth.has(parentId)) fail(400, "Each branch must come after its parent.");
    if (depth.get(parentId) + 1 > MAX_DEPTH) fail(400, `Branches can go up to ${MAX_DEPTH} levels deep.`);
    depth.set(id, depth.get(parentId) + 1);
    const { score } = validateScore(node.score);
    measures += score.measures.length;
    return { id, parentId, name: string(node.name, 60, "branch name", true), score };
  });
  if (measures > MAX_TREE_MEASURES) fail(400, "This rhythm tree is too large to save. Try fewer or shorter branches.");
  return nodes.length ? { nodes } : null;
}
export function normalizeMutations(value = []) {
  if (!Array.isArray(value) || value.length > 1) fail(400, "Invalid dynamic rhythm settings.");
  return value.map((entry) => {
    const config = typeof entry.config === "string" ? entry.config : JSON.stringify(entry.config || {});
    if (config.length > 12000) fail(400, "Dynamic rhythm settings are too large.");
    let parsed;
    try { parsed = JSON.parse(config); } catch { fail(400, "Invalid dynamic rhythm settings."); }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed) || (parsed.probability != null && (!Number.isFinite(parsed.probability) || parsed.probability < 0 || parsed.probability > 1))) fail(400,"Invalid dynamic rhythm settings.");
    if (![1,2,4,8,16,32,64,128].includes(entry.grid)) fail(400, "Invalid dynamic rhythm grid.");
    const type = enumValue(entry.type,["swap","shuffle","reverse",...Object.values(automataTypes)],"mutation type");
    const context = string(entry.context,60,"mutation context",true);
    if (context !== "All" && !/^[A-G][#b]?-?\d$/.test(context)) fail(400,"Invalid mutation context.");
    return { type, context, grid: entry.grid, config };
  });
}
export function searchOptions(query = {}) {
  const type = enumValue(query.type || "rhythms", ["rhythms", "users"], "search type");
  const raw = string(query.q, 100, "search");
  const q = type === "users" ? raw.replace(/^@/, "").trim() : raw;
  if (Array.isArray(query.page) || typeof query.page === "object") fail(400,"Invalid result page.");
  const page = Number(query.page || 1);
  if (!Number.isInteger(page) || page < 1 || page > 100) fail(400, "Invalid result page.");
  const instrument = query.instrument ? enumValue(query.instrument, INSTRUMENT_IDS, "instrument") : "";
  const difficulty = query.difficulty ? enumValue(query.difficulty, DIFFICULTIES, "difficulty") : "";
  // Ideas written as notation (or played in as MIDI), or recordings.
  const medium = query.medium ? enumValue(query.medium, ["notation", "recording"], "kind of idea") : "";
  return { q, type, page, instrument, difficulty, medium, limit: 12 };
}

// A recording idea's sound: what's in it (which part of the Rhythm Graph it belongs to), how long
// it is, and its waveform (up to 240 levels, 0–1) for drawing it.
export function normalizeRecording(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) fail(400, "Describe the recording.");
  const duration = Number(value.duration);
  if (!Number.isFinite(duration) || duration < 0.5 || duration > 3600) fail(400, "A recording can be half a second to an hour long.");
  if (!Array.isArray(value.waveform) || value.waveform.length < 8 || value.waveform.length > 240 || value.waveform.some((level) => !Number.isFinite(level) || level < 0 || level > 1)) fail(400, "Invalid waveform.");
  return { duration: Math.round(duration * 100) / 100, waveform: value.waveform.map((level) => Math.round(level * 1000) / 1000),
    sound: enumValue(value.sound || "other", Object.keys(RECORDING_SOUNDS), "sound") };
}
